import { Component, EventEmitter, Input, Output, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { SchedulingSubject } from '../../../core/interfaces/academic-appointment';
import { SaveInspectionScope } from '../../../core/interfaces/inspection-scope';
import { SubjectChoiceService, SubjectPathGroup } from '../../../core/services/utils/subject-choice.service';

/**
 * Composer un périmètre d'inspection : tout l'établissement, ou des matières
 * choisies niveau par niveau — « tout le niveau » coche d'un geste.
 *
 * <p>Les niveaux et leurs matières viennent du référentiel que l'appelant
 * fournit ; le composant ne connaît ni le compte ni l'API. Il émet la valeur
 * à chaque changement, l'enregistrement appartient à l'écran qui l'héberge.</p>
 */
@Component({
  selector: 'app-inspection-scope-editor',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './inspection-scope-editor.component.html',
  styleUrl: './inspection-scope-editor.component.css',
})
export class InspectionScopeEditorComponent {
  private readonly subjectChoice = inject(SubjectChoiceService);

  private readonly referential = signal<SchedulingSubject[]>([]);
  readonly all = signal(false);
  /** Matières cochées, par niveau. */
  readonly selected = signal<Map<number, Set<number>>>(new Map());

  @Input({ required: true }) set subjects(subjects: SchedulingSubject[]) {
    this.referential.set(subjects ?? []);
  }

  @Input() set value(value: SaveInspectionScope | null | undefined) {
    const next: SaveInspectionScope = { all: value?.all === true, items: value?.items ?? [] };
    // L'écho de ce qu'on vient d'émettre — le parent lie la valeur qu'il
    // reçoit — ne touche pas à la sélection : « tout l'établissement » émet
    // une liste vide, et la relire effacerait ce qui est coché par niveau.
    if (this.sameAs(next)) return;
    this.all.set(next.all);
    const map = new Map<number, Set<number>>();
    for (const item of next.items) {
      const set = map.get(item.learningPathId) ?? new Set<number>();
      set.add(item.subjectId);
      map.set(item.learningPathId, set);
    }
    this.selected.set(map);
  }

  @Output() readonly valueChange = new EventEmitter<SaveInspectionScope>();

  readonly levels = computed<SubjectPathGroup[]>(() => this.subjectChoice.groupByPath(this.referential()));

  subjectLabel(s: SchedulingSubject): string {
    return this.subjectChoice.label(s, this.referential());
  }

  isChecked(level: SubjectPathGroup, subject: SchedulingSubject): boolean {
    return this.selected().get(level.pathId)?.has(subject.id) ?? false;
  }

  countOf(level: SubjectPathGroup): number {
    return this.selected().get(level.pathId)?.size ?? 0;
  }

  isWholeLevel(level: SubjectPathGroup): boolean {
    return level.subjects.length > 0 && this.countOf(level) === level.subjects.length;
  }

  chooseAll(): void {
    this.all.set(true);
    this.emit();
  }

  chooseByLevel(): void {
    this.all.set(false);
    this.emit();
  }

  toggle(level: SubjectPathGroup, subject: SchedulingSubject): void {
    this.update(level.pathId, (set) => { set.has(subject.id) ? set.delete(subject.id) : set.add(subject.id); });
  }

  toggleLevel(level: SubjectPathGroup): void {
    const whole = this.isWholeLevel(level);
    this.update(level.pathId, (set) => {
      set.clear();
      if (!whole) level.subjects.forEach((s) => set.add(s.id));
    });
  }

  private update(pathId: number, change: (set: Set<number>) => void): void {
    this.selected.update((map) => {
      const next = new Map(map);
      const set = new Set(next.get(pathId) ?? []);
      change(set);
      set.size ? next.set(pathId, set) : next.delete(pathId);
      return next;
    });
    this.emit();
  }

  private emit(): void {
    this.valueChange.emit(this.snapshot());
  }

  private sameAs(value: SaveInspectionScope): boolean {
    const current = this.snapshot();
    if (current.all !== value.all) return false;
    const key = (i: SaveInspectionScope['items'][number]) => `${i.learningPathId}:${i.subjectId}`;
    const mine = new Set(current.items.map(key));
    return mine.size === value.items.length && value.items.every((i) => mine.has(key(i)));
  }

  /** La valeur courante, telle que le serveur l'attend. */
  snapshot(): SaveInspectionScope {
    if (this.all()) return { all: true, items: [] };
    const items: SaveInspectionScope['items'] = [];
    for (const [learningPathId, set] of this.selected()) {
      for (const subjectId of set) items.push({ learningPathId, subjectId });
    }
    return { all: false, items };
  }
}
